/**
 * Runtime-Config-Aware Fetch Client
 *
 * Centralizes API requests to the backend (Cloudflare Workers API) so that
 * the base URL and auth header wiring live in one place instead of being
 * re-implemented ad hoc in every component (as in, e.g., the fetch calls in
 * `src/app/routes/admin/Subscriptions.tsx`).
 *
 * Uses the runtime configuration system (`@/lib/config/runtimeConfig`,
 * `@/app/providers`) for `apiBaseUrl` instead of a build-time
 * `import.meta.env.VITE_API_*` variable, so the same built SPA can target a
 * different API origin per environment (dev/stage/prod) without a rebuild.
 * See docs/v2/05-runtime-configuration.md.
 *
 * NOTE: This is intentionally a small, dependency-free wrapper around
 * `fetch`. It is superseded by RTK Query once the "Unified API client with
 * RTK Query for all frontend data fetching" issue lands; existing call
 * sites are not required to migrate immediately.
 */
import { useCallback, useMemo } from 'react';
import { useAuth, useConfig } from '@/app/providers';

/**
 * Error thrown for non-2xx API responses.
 *
 * Carries the HTTP status and (when available) the parsed JSON error body
 * so callers can branch on specific error codes without re-parsing.
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly body?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Options accepted by `apiFetch` / the `useApiFetch()` hook's returned
 * function. A subset of `RequestInit` plus a couple of conveniences.
 */
export interface ApiFetchOptions extends Omit<RequestInit, 'body'> {
  /** JSON-serializable body. Automatically stringified and given a
   * `Content-Type: application/json` header (unless already set). */
  json?: unknown;
  /** Raw body, bypasses JSON serialization (e.g. FormData). */
  body?: RequestInit['body'];
}

/**
 * Build the full request URL for a path against a configured API base URL.
 *
 * Handles base URLs with or without a trailing slash and paths with or
 * without a leading slash, so callers can pass either `'/jobs'` or `'jobs'`.
 *
 * @param apiBaseUrl - Runtime-config API base URL (e.g. from `useConfig()`)
 * @param path - Path to append, relative to `apiBaseUrl`
 */
export function buildApiUrl(apiBaseUrl: string, path: string): string {
  const base = apiBaseUrl.replace(/\/+$/, '');
  const suffix = path.startsWith('/') ? path : `/${path}`;
  return `${base}${suffix}`;
}

/**
 * Perform a fetch against the API using an explicit base URL and optional
 * bearer token.
 *
 * This is the non-hook entry point — useful outside of React components
 * (e.g. in tests, or plain utility functions) where `apiBaseUrl` is passed
 * in directly rather than read via `useConfig()`.
 *
 * @param apiBaseUrl - Runtime config `apiBaseUrl` value
 * @param path - Path relative to `apiBaseUrl` (e.g. `/jobs`)
 * @param options - Fetch options, plus optional `json` body and `accessToken`
 * @throws ApiError if the response status is not ok (2xx)
 */
export async function apiFetch<T = unknown>(
  apiBaseUrl: string,
  path: string,
  options: ApiFetchOptions & { accessToken?: string | null } = {}
): Promise<T> {
  const { json, body, accessToken, headers, ...rest } = options;

  const finalHeaders = new Headers(headers);
  if (json !== undefined && !finalHeaders.has('Content-Type')) {
    finalHeaders.set('Content-Type', 'application/json');
  }
  if (accessToken && !finalHeaders.has('Authorization')) {
    finalHeaders.set('Authorization', `Bearer ${accessToken}`);
  }

  const response = await fetch(buildApiUrl(apiBaseUrl, path), {
    ...rest,
    headers: finalHeaders,
    body: json !== undefined ? JSON.stringify(json) : body,
  });

  // 204 No Content / empty bodies — nothing to parse.
  const rawText = await response.text();
  let parsed: unknown = undefined;
  if (rawText) {
    try {
      parsed = JSON.parse(rawText);
    } catch {
      // Non-JSON response body; surface as-is.
      parsed = rawText;
    }
  }

  if (!response.ok) {
    const message =
      (parsed && typeof parsed === 'object' && 'error' in parsed
        ? String((parsed as { error?: unknown }).error)
        : undefined) || `Request to ${path} failed (HTTP ${response.status})`;
    throw new ApiError(message, response.status, parsed);
  }

  return parsed as T;
}

/**
 * React hook returning an `apiFetch`-bound function that automatically
 * uses the runtime config's `apiBaseUrl` and the current Supabase session's
 * access token (if any).
 *
 * Must be called from a component nested under both `ConfigProvider` and
 * `AuthProvider` (e.g. anything rendered under `AppShell` — see
 * `src/app/AppShell.tsx`).
 *
 * @example
 * function JobsList() {
 *   const apiFetch = useApiFetch();
 *   useEffect(() => {
 *     apiFetch('/jobs').then(setJobs).catch(handleError);
 *   }, [apiFetch]);
 * }
 */
export function useApiFetch() {
  const config = useConfig();
  const { session } = useAuth();

  return useCallback(
    <T = unknown,>(path: string, options: ApiFetchOptions = {}) =>
      apiFetch<T>(config.apiBaseUrl, path, {
        ...options,
        accessToken: session?.access_token ?? null,
      }),
    [config.apiBaseUrl, session?.access_token]
  );
}

/**
 * React hook returning a small object with `get`/`post`/`put`/`patch`/
 * `delete` convenience methods, all bound to the runtime-config API base
 * URL and current session token. Thin sugar over `useApiFetch()`.
 */
export function useApiClient() {
  const request = useApiFetch();

  return useMemo(
    () => ({
      get: <T = unknown,>(path: string, options?: ApiFetchOptions) =>
        request<T>(path, { ...options, method: 'GET' }),
      post: <T = unknown,>(path: string, json?: unknown, options?: ApiFetchOptions) =>
        request<T>(path, { ...options, method: 'POST', json }),
      put: <T = unknown,>(path: string, json?: unknown, options?: ApiFetchOptions) =>
        request<T>(path, { ...options, method: 'PUT', json }),
      patch: <T = unknown,>(path: string, json?: unknown, options?: ApiFetchOptions) =>
        request<T>(path, { ...options, method: 'PATCH', json }),
      delete: <T = unknown,>(path: string, options?: ApiFetchOptions) =>
        request<T>(path, { ...options, method: 'DELETE' }),
    }),
    [request]
  );
}
